import dynamic from 'next/dynamic';
import { Link, Text } from '@thebouqs/ui';
import { MenuLinkProps } from './MenuLink.d';
import useAnalytics from '@/hooks/useAnalytics';
import useUser from '@/hooks/useUser';
import { linkChildren, linkName } from '@/utils/navigation';
import { AnalyticsEvent } from '@/store/analytics/AnalyticsContext';

const SubMenu = dynamic(() => import('../SubMenu'), { ssr: false });

const SubMenuLink = ({ data }: Pick<MenuLinkProps, 'data'>): JSX.Element => {
  const { name, link, is_heading } = data;
  const { track } = useAnalytics();

  const handleTracking = name => {
    track(AnalyticsEvent.PRIMARY_NAVIGATION_CLICK, {
      label: name,
    });
  };

  if (is_heading && !link) {
    return (
      <Text
        className="mega-menu__item mega-menu__item--sub mega-menu__item--heading mega-menu__item--section-heading"
        role="heading"
        aria-level={2}>
        {linkName(name)}
      </Text>
    );
  } else if (is_heading && link) {
    return (
      <Link
        href={link}
        className="mega-menu__item mega-menu__item--sub mega-menu__item--link mega-menu__item--heading">
        {linkName(name)}
      </Link>
    );
  } else if (link) {
    return (
      <Link
        onClick={() => handleTracking(name)}
        href={link}
        className="mega-menu__item mega-menu__item--sub mega-menu__item--link">
        {linkName(name)}
      </Link>
    );
  } else {
    return (
      <Text className="mega-menu__item mega-menu__item--placeholder">
        {linkName(name)}
      </Text>
    );
  }
};

const TopMenuLink = ({ data, topLinkIndex }: MenuLinkProps): JSX.Element => {
  const { name, children, link, content } = data;
  const { is_subscriber: isSubscriber } = useUser();

  const childrenLinks = [
    'subscriptions',
    'my subscriptions',
    'my subscription',
  ].includes(name.toLowerCase())
    ? linkChildren(children, isSubscriber)
    : children;

  if (childrenLinks?.length > 0) {
    return (
      <SubMenu
        topLinkPath={link}
        topLinkIndex={topLinkIndex}
        subMenuLinks={childrenLinks}
        topLinkName={linkName(name)}
        subMenuContent={content}
      />
    );
  } else {
    return (
      <Link
        href={link}
        className="mega-menu__item mega-menu__item--link mega-menu__item--top">
        {linkName(name)}
      </Link>
    );
  }
};

const MenuLink = ({
  variant,
  data,
  topLinkIndex,
}: MenuLinkProps): JSX.Element => {
  const variants = {
    top: <TopMenuLink data={data} topLinkIndex={topLinkIndex} />,
    sub: <SubMenuLink data={data} />,
  };
  return variants[variant];
};

export default MenuLink;
